import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import api from '../api';

const AuthContext = createContext(null);

// Plan display helpers — keep in sync with backend/lib/planLimits.js
export const PLAN_META = {
  starter: { label: 'Starter', color: '#6b6560', badge: 'Free',  maxProjects: 1,        maxMonthlyChecks: 10,       customTone: false, ledger: false, teamHub: false },
  pro:     { label: 'Pro',     color: '#5b46f5', badge: 'Pro',   maxProjects: 5,        maxMonthlyChecks: Infinity, customTone: true,  ledger: true,  teamHub: false },
  teams:   { label: 'Teams',   color: '#16a34a', badge: 'Teams', maxProjects: Infinity, maxMonthlyChecks: Infinity, customTone: true,  ledger: true,  teamHub: true  },
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function AuthProvider({ children }) {
  const [user, setUser]           = useState(null);
  const [profile, setProfile]     = useState(null);
  const [session, setSession]     = useState(null);
  const [loading, setLoading]     = useState(true);   // true until first session check

  // ── On mount: restore session + subscribe to auth changes ──────────────────
  useEffect(() => {
    // 1. Get current session immediately
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) fetchProfile(s.user.id);
      else setLoading(false);
    });

    // 2. Subscribe to future auth events (OAuth callback, sign-out, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) fetchProfile(s.user.id);
      else { setProfile(null); setLoading(false); }
    });

    return () => subscription.unsubscribe();
  }, []);

  // ── Attach JWT to all API calls ────────────────────────────────────────────
  useEffect(() => {
    const interceptor = api.interceptors.request.use(async (config) => {
      const { data: { session: s } } = await supabase.auth.getSession();
      if (s?.access_token) {
        config.headers['Authorization'] = `Bearer ${s.access_token}`;
      }
      return config;
    });
    return () => api.interceptors.request.eject(interceptor);
  }, []);

  async function fetchProfile(userId) {
    try {
      const res = await api.get('/auth/me');
      setProfile(res.data);
    } catch {
      // Profile API unavailable — use minimal profile
      setProfile({ id: userId, plan: 'starter' });
    } finally {
      setLoading(false);
    }
  }

  // ── Google OAuth login ─────────────────────────────────────────────────────
  const loginWithGoogle = useCallback(async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/app`,
        queryParams: { access_type: 'offline', prompt: 'consent' },
      },
    });
    if (error) return { success: false, error: error.message };
    return { success: true };
  }, []);

  // ── Sign out ───────────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setSession(null);
  }, []);

  // ── Computed plan meta ─────────────────────────────────────────────────────
  const planMeta = profile ? (PLAN_META[profile.plan] || PLAN_META.starter) : PLAN_META.starter;

  const canUseFeature = useCallback((feature) => {
    if (!user) return false;
    const val = planMeta[feature];
    return val === true || (typeof val === 'number' && val > 0);
  }, [user, planMeta]);

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      session,
      loading,
      loginWithGoogle,
      logout,
      planMeta,
      canUseFeature,
      // Legacy compat: expose user with plan for components that use user.plan
      userWithPlan: user ? { ...user, plan: profile?.plan || 'starter', name: profile?.name || user.email } : null,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
